/**
 * @file
 * Toast Coins main class that handles Drupal behavior and scene management.
 */

import { gsap } from 'gsap';
import SceneManager from './SceneManager.js';

/**
 * Check if user prefers reduced motion
 * @returns {boolean}
 */
const prefersReducedMotion = () => {
  return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

// Shared canvas manager for all toast-coins instances
class SharedCanvasManager {
  static instance = null;
  static canvasWrapper = null;
  static scene = null;
  static activeToasts = new Set();
  static resizeObserver = null;
  static tickerListener = null;

  static getCanvas() {
    // Don't create canvas if user prefers reduced motion
    if (prefersReducedMotion()) {
      return null;
    }

    if (!this.canvasWrapper) {
      const canvasId = 'toast-coins-canvas';
      this.canvasWrapper = document.createElement('div');
      this.canvasWrapper.id = canvasId;
      this.canvasWrapper.className = 'toast-coins__canvas-wrapper';

      const canvas = document.createElement('canvas');
      canvas.className = 'toast-coins__canvas';
      this.canvasWrapper.appendChild(canvas);

      document.body.appendChild(this.canvasWrapper);
      this.canvasWrapper.style.display = 'block';

      // Initialize scene (no coins yet - will be created when toast is registered)
      this.scene = new SceneManager(canvas, 0);
      this.scene.init();

      // Handle resize
      this.resizeObserver = new ResizeObserver(() => {
        if (this.scene) {
          this.scene.resize();
        }
      });
      this.resizeObserver.observe(canvas);

      // Start animation loop using GSAP ticker
      this.startAnimation();
    }
    return this.canvasWrapper;
  }

  /**
   * Animation loop using GSAP ticker
   * Runs after GSAP's global timeline updates, sharing the same RAF
   * @param {number} time - Total time in seconds since ticker started
   * @param {number} deltaTime - Milliseconds elapsed since previous tick
   * @param {number} frame - Frame number
   */
  static animate(time, deltaTime, frame) {
    // Only render if we have a scene and active toasts
    if (this.scene && this.activeToasts.size > 0) {
      this.scene.render();
    }
  }

  static startAnimation() {
    if (this.tickerListener) return; // Already animating

    // Use GSAP ticker to share RAF with GSAP timeline
    // The ticker runs after GSAP's global timeline updates
    this.tickerListener = this.animate.bind(this);
    gsap.ticker.add(this.tickerListener);
  }

  static stopAnimation() {
    if (this.tickerListener) {
      gsap.ticker.remove(this.tickerListener);
      this.tickerListener = null;
    }
  }

  static registerToast(toastId, coinCount, toastElement) {
    // Don't register if user prefers reduced motion
    if (prefersReducedMotion()) {
      return;
    }

    this.activeToasts.add(toastId);

    // Ensure canvas and scene exist
    if (!this.scene) {
      this.getCanvas();
    }

    // Always recreate coins when a new toast is registered
    // This ensures the animation retriggers for new toasts
    if (this.scene) {
      this.scene.coinCount = Math.max(1, Math.min(coinCount, 25));
      this.scene.toastElement = toastElement;
      this.scene.createCoins();
    }

    // Ensure animation is running
    this.startAnimation();
  }

  static unregisterToast(toastId) {
    this.activeToasts.delete(toastId);

    // Only destroy canvas if no toasts are active
    if (this.activeToasts.size === 0) {
      this.stopAnimation();

      if (this.scene) {
        this.scene.destroy();
        this.scene = null;
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }

      if (this.canvasWrapper && this.canvasWrapper.parentNode) {
        this.canvasWrapper.parentNode.removeChild(this.canvasWrapper);
        this.canvasWrapper = null;
      }
    }
  }
}

export default class ToastCoins {
  constructor(element) {
    this.element = element;
    this.toastId = null;
    this.coinCount = 1;
    this.observer = null;
    this.parentObserver = null;
  }

  init() {
    // Don't initialize scene if user prefers reduced motion
    if (prefersReducedMotion()) {
      return;
    }

    // Get points count from toast element data attribute or default to 1
    this.coinCount = parseInt(this.element.getAttribute('data-points') || '1', 10) || 1;
    this.toastId = this.element.getAttribute('data-toast-id') || `toast-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

    // Store toast ID on element for cleanup
    this.element.setAttribute('data-toast-id', this.toastId);

    // Get or create shared canvas (this creates the scene if it doesn't exist)
    SharedCanvasManager.getCanvas();

    // Register this toast with the shared canvas manager
    SharedCanvasManager.registerToast(this.toastId, this.coinCount, this.element);

    // Clean up on toast dismissal
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          if (this.element.classList.contains('advanced-toast--hiding')) {
            this.destroy();
          }
        }
      });
    });

    this.observer.observe(this.element, {
      attributes: true,
      attributeFilter: ['class'],
    });

    // Also clean up if element is removed
    this.parentObserver = new MutationObserver(() => {
      if (!document.body.contains(this.element)) {
        this.destroy();
      }
    });

    this.parentObserver.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Animation loop
   * Called by SharedCanvasManager, but kept here for consistency with SpinToWin pattern
   */
  animate() {
    // Animation is handled by SharedCanvasManager
    // This method exists for API consistency with SpinToWin
  }

  destroy() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    if (this.parentObserver) {
      this.parentObserver.disconnect();
      this.parentObserver = null;
    }

    // Unregister this toast from shared canvas manager
    // Canvas will only be destroyed when last toast is dismissed
    if (this.toastId) {
      SharedCanvasManager.unregisterToast(this.toastId);
    }
  }
}

